import React from "react";
import { Link } from "react-router-dom";
import {
    Briefcase, FileText, Calendar, FolderKanban, GraduationCap, Database, ArrowRight, Check
} from "lucide-react";
import { useDocumentSeo, useJsonLd } from "../lib/seo";

const SERVICES = [
    {
        icon: <Briefcase className="w-7 h-7" />,
        title: "Administrative & Executive Support",
        body: "Day-to-day administrative excellence — inbox management, calendar coordination, client correspondence, travel and meeting logistics.",
        bullets: ["Inbox & calendar", "Client correspondence", "Travel & logistics"],
        accent: "bg-[var(--brand-sky)]",
    },
    {
        icon: <FolderKanban className="w-7 h-7" />,
        title: "Business Strategy & Consulting",
        body: "Strategy and structure for organizations called to operate in purpose. We bring clarity, frameworks, and growth roadmaps.",
        bullets: ["Operations sync", "Growth strategy", "Process design"],
        accent: "bg-[var(--brand-coral)]",
    },
    {
        icon: <FileText className="w-7 h-7" />,
        title: "Document Organization & Data Management",
        body: "Bring order to your records. Document preparation, secure storage workflows, and data management protocols.",
        bullets: ["Doc preparation", "Records workflows", "Compliance ready"],
        accent: "bg-[var(--brand-gold)]",
    },
    {
        icon: <Calendar className="w-7 h-7" />,
        title: "Scheduling & Client Coordination",
        body: "Be present for what matters. We handle the orchestration — appointments, follow-ups, and reminders.",
        bullets: ["Appointment booking", "Follow-up flows", "Client onboarding"],
        accent: "bg-[var(--brand-sky)]",
    },
    {
        icon: <GraduationCap className="w-7 h-7" />,
        title: "Business Formation & Startup Structuring",
        body: "From DBA to LLC to operating agreements — we set new ventures on a strong, compliant foundation.",
        bullets: ["Entity formation", "EIN & licenses", "Operating agreements"],
        accent: "bg-[var(--brand-coral)]",
    },
    {
        icon: <Database className="w-7 h-7" />,
        title: "Workforce Training & OJT Support",
        body: "On-the-Job Training program design and grant readiness — especially for veteran and underserved workforces.",
        bullets: ["OJT design", "Grant readiness", "Federal partnership"],
        accent: "bg-[var(--brand-gold)]",
    },
];

export default function Services() {
    useDocumentSeo({
        title: "Business Services — Administrative Support, Consulting & LLC Formation | TT Admins",
        description: "Executive administrative support, business consulting, LLC & entity formation, document management, scheduling, and OJT program design. SBA-certified WOSB & VOSB. Federal contract-ready. Lewisville, TX.",
        canonical: "https://www.ttadmins.com/services",
    });
    useJsonLd([
        {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: "https://www.ttadmins.com/" },
                { "@type": "ListItem", position: 2, name: "Business Services", item: "https://www.ttadmins.com/services" },
            ],
        },
        {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "Business Services",
            itemListElement: SERVICES.map((s, i) => ({
                "@type": "ListItem",
                position: i + 1,
                item: {
                    "@type": "Service",
                    name: s.title,
                    description: s.body,
                    provider: {
                        "@type": "ProfessionalService",
                        name: "Admin This! N Thatz Syncd, LLC",
                        url: "https://www.ttadmins.com",
                    },
                    areaServed: { "@type": "Country", name: "United States" },
                },
            })),
        },
    ]);
    return (
        <div data-testid="services-page">
            {/* Hero */}
            <section className="dawn-wash">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                    <div className="max-w-3xl">
                        <div className="eyebrow mb-3">Business Services</div>
                        <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] font-light text-[var(--brand-navy)]">
                            Stewardship, <span className="italic text-[var(--brand-coral)]">excellence</span>, and the gift of order.
                        </h1>
                        <p className="mt-6 text-lg lg:text-xl text-slate-700 leading-relaxed">
                            Operating in your purpose is hard when the back-office is unstructured. We bring administrative precision and faith-led service so you can do the work you were called to do.
                        </p>
                        <div className="mt-10 flex flex-wrap gap-4">
                            <Link to="/contact" className="btn-gold" data-testid="services-hero-book-btn">
                                Book a Consultation <ArrowRight className="w-4 h-4 ml-2" />
                            </Link>
                            <a href="/capability-statement.pdf" target="_blank" rel="noopener noreferrer" className="btn-outline-navy" data-testid="services-capability-btn">
                                Download Capability Statement
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* Services grid */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
                    {SERVICES.map((s, idx) => (
                        <div key={s.title} className={`faith-card p-8 fade-up delay-${(idx % 4) + 1}`} data-testid={`service-card-${idx}`}>
                            <div className={`w-14 h-14 rounded-2xl ${s.accent} text-white flex items-center justify-center mb-6`}>
                                {s.icon}
                            </div>
                            <h3 className="font-serif text-2xl text-[var(--brand-navy)] mb-3">{s.title}</h3>
                            <p className="text-slate-600 text-sm leading-relaxed mb-5">{s.body}</p>
                            <ul className="space-y-2 mb-7">
                                {s.bullets.map((b) => (
                                    <li key={b} className="flex items-start text-sm text-slate-700">
                                        <Check className="w-4 h-4 mt-0.5 mr-2 text-[var(--brand-coral)] shrink-0" /> {b}
                                    </li>
                                ))}
                            </ul>
                            <Link to="/contact" className="inline-flex items-center text-sm font-semibold text-[var(--brand-navy)] hover:text-[var(--brand-coral)]" data-testid={`service-book-${idx}`}>
                                Book this service <ArrowRight className="w-4 h-4 ml-1.5" />
                            </Link>
                        </div>
                    ))}
                </div>
            </section>

            {/* How we work */}
            <section className="sky-wash py-20 lg:py-28">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl mb-14">
                        <div className="eyebrow mb-3">How we work</div>
                        <h2 className="font-serif text-4xl sm:text-5xl leading-tight text-[var(--brand-navy)]">A clear, deliberate, four-step engagement.</h2>
                    </div>
                    <div className="grid md:grid-cols-4 gap-6">
                        {[
                            ["01", "Discover", "A no-cost consultation to understand your business, your goals, and the operational gaps you're navigating."],
                            ["02", "Design", "We propose a synchronized scope of work and clear deliverables."],
                            ["03", "Deliver", "We execute with excellence — communication is constant."],
                            ["04", "Sync", "We continually refine systems so the mission outlives the project."],
                        ].map(([num, t, b], i) => (
                            <div key={num} className={`faith-card p-7 fade-up delay-${i + 1}`}>
                                <div className="text-5xl font-serif font-light text-[var(--brand-gold-deep)] mb-3">{num}</div>
                                <div className="font-serif text-xl text-[var(--brand-navy)] mb-2">{t}</div>
                                <div className="text-sm text-slate-600 leading-relaxed">{b}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>
        </div>
    );
}
